import { useExcelReader } from "../../file-reader";
import { Cell } from "exceljs";
import {
  strictCellValueTypeIsHyperlink,
  strictCellValueTypeIsRichText,
  strictCellValueTypeIsString,
} from "./types";

export const getCellStyle = (
  index: number,
  cellIndex: number,
  excelReader: ReturnType<typeof useExcelReader>
): React.CSSProperties => {
  const cell = excelReader.getCell(index, cellIndex);
  const style = cell?.style;
  if (!style || !cell) {
    return {};
  }

  const cssProperties: React.CSSProperties = {};

  const convertExcelColorToHex = (argb: string): string => {
    if (!argb) {
      return "";
    }
    // Remove the alpha channel (first two characters) and add # prefix
    return `#${argb.slice(2)}`;
  };

  // Column width - use min-width instead of fixed width
  const column = excelReader.currentSheet?.getColumn(cellIndex + 1);
  cssProperties.minWidth = "100px";
  if (column?.width) {
    cssProperties.minWidth = `${column.width}px`;
  }

  // Text wrapping and shrinking
  if (style.alignment) {
    if (style.alignment.wrapText) {
      cssProperties.whiteSpace = "normal";
      cssProperties.wordWrap = "break-word";
    } else {
      cssProperties.whiteSpace = "nowrap";
    }

    if (style.alignment.shrinkToFit) {
      cssProperties.fontSize = "100%";
      cssProperties.overflow = "hidden";
      cssProperties.textOverflow = "ellipsis";
    }
  }

  // Font properties
  if (style.font) {
    if (style.font.bold) {
      cssProperties.fontWeight = "bold";
    }
    if (style.font.italic) {
      cssProperties.fontStyle = "italic";
    }
    if (style.font.underline) {
      cssProperties.textDecoration = "underline";
    }
    if (style.font.size) {
      cssProperties.fontSize = `${style.font.size}px`;
    }
    if (style.font.color) {
      cssProperties.color = convertExcelColorToHex(style.font.color.argb);
    }
  }

  // Alignment
  if (style.alignment) {
    if (style.alignment.horizontal) {
      const horizontalAlign = style.alignment.horizontal;
      if (
        horizontalAlign === "left" ||
        horizontalAlign === "center" ||
        horizontalAlign === "right"
      ) {
        cssProperties.textAlign = horizontalAlign;
      }
    }
    if (style.alignment.vertical) {
      const verticalAlign = style.alignment.vertical;
      if (
        verticalAlign === "top" ||
        verticalAlign === "middle" ||
        verticalAlign === "bottom"
      ) {
        cssProperties.verticalAlign = verticalAlign;
      }
    }
  }

  // Fill (background color)
  if (style.fill) {
    if (style.fill.type === "pattern") {
      if (style.fill.fgColor) {
        cssProperties.backgroundColor = convertExcelColorToHex(
          style.fill.fgColor.argb
        );
      }
      if (style.fill.bgColor) {
        cssProperties.backgroundColor = convertExcelColorToHex(
          style.fill.bgColor.argb
        );
      }
    } else if (style.fill.type === "gradient") {
      // Handle gradient fills if needed
      if (style.fill.gradient === "angle") {
        const stops = style.fill.stops;
        if (stops && stops.length > 0) {
          const gradientStops = stops
            .map(
              (stop) =>
                `${convertExcelColorToHex(stop.color.argb)} ${
                  stop.position * 100
                }%`
            )
            .join(", ");
          cssProperties.background = `linear-gradient(${style.fill.degree}deg, ${gradientStops})`;
        }
      }
    }
  }

  return cssProperties;
};

export const getRowStyle = (
  index: number,
  excelReader: ReturnType<typeof useExcelReader>
): React.CSSProperties => {
  const row = excelReader.getRow(index);
  if (!row) return {};

  const style: React.CSSProperties = {};

  // Height
  if (row.height) {
    style.height = row.height;
  }

  return style;
};

export const SEARCH_INPUT_ID = "txu-excel-file-viewer-search-input";
export const SEARCH_VALUE_ID = "txu-excel-file-viewer-search-value";

export const updateInputOnFocus = (event: FocusEvent): void => {
  const element = event.target as HTMLElement;
  const searchInput = document.getElementById(
    SEARCH_INPUT_ID
  ) as HTMLInputElement;
  const searchValue = document.getElementById(
    SEARCH_VALUE_ID
  ) as HTMLSpanElement;
  if (!searchInput || !searchValue) return;
  if (
    element.classList.contains("txu-excel-file-viewer-letter") ||
    element.classList.contains("txu-excel-file-viewer-number")
  ) {
    searchInput.value = "";
    searchValue.textContent = "";
    return;
  }
  let columnIndex: number | null = null;
  let rowIndex: number | null = null;
  if (element.dataset.column) {
    columnIndex = parseInt(element.dataset.column);
  }
  if (element.dataset.row) {
    rowIndex = parseInt(element.dataset.row);
  }
  if (columnIndex && rowIndex) {
    if (searchInput) {
      searchInput.value = `${String.fromCharCode(64 + columnIndex)}${rowIndex}`;
    }
    if (searchValue) {
      searchValue.textContent = element.textContent;
    }
  }
};

const getSelector = (
  type: "number" | "letter" | "column" | "row",
  index?: number
): string => {
  if (index) {
    return `[data-${type}='${index}']`;
  }
  return `[data-${type}]`;
};

const onEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
  event.preventDefault();
  event.stopPropagation();
  const value = event.currentTarget.value;
  try {
    const column = value.toUpperCase().charCodeAt(0) - 64;
    const row = parseInt(value.slice(1));
    if (column && row) {
      const element = document.querySelector<HTMLTableCellElement>(
        getSelector("column", column) + getSelector("row", row)
      );
      if (element) {
        element.focus();
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }
    }
  } catch (error) {
    console.error(error);
  }
};

export const onInputKeyDown = (
  event: React.KeyboardEvent<HTMLInputElement>
): void => {
  if (event.key === "Enter") {
    onEnter(event);
  }
};

export const toggleNumberFocus = (rowNumber: number, on: boolean): void => {
  const selectorRow = getSelector("row", rowNumber);
  const selectorLetters = getSelector("letter");
  const elementsToFocusRow = document.querySelectorAll(selectorRow);
  elementsToFocusRow.forEach((element) => {
    if (on) {
      element.classList.add("txu-focused-row");
    } else {
      element.classList.remove("txu-focused-row");
    }
  });
  const elementsToFocusLetters = document.querySelectorAll(selectorLetters);
  elementsToFocusLetters.forEach((element) => {
    if (on) {
      element.classList.add("txu-focused");
    } else {
      element.classList.remove("txu-focused");
    }
  });
};

export const toggleLetterFocus = (cellNumber: number, on: boolean): void => {
  const selectorColumn = getSelector("column", cellNumber);
  const selectorNumbers = getSelector("number");
  const elementsToFocusColumn = document.querySelectorAll(selectorColumn);
  elementsToFocusColumn.forEach((element) => {
    if (on) {
      element.classList.add("txu-focused-column");
    } else {
      element.classList.remove("txu-focused-column");
    }
  });
  const elementsToFocusNumbers = document.querySelectorAll(selectorNumbers);
  elementsToFocusNumbers.forEach((element) => {
    if (on) {
      element.classList.add("txu-focused");
    } else {
      element.classList.remove("txu-focused");
    }
  });
};

export const toggleCellFocus = (
  columnIndex: number,
  rowIndex: number,
  on: boolean
): void => {
  if (on) {
    document
      .querySelector(getSelector("letter", columnIndex))
      ?.classList.add("txu-focused");
    document
      .querySelector(getSelector("number", rowIndex))
      ?.classList.add("txu-focused");
  } else {
    document
      .querySelector(getSelector("letter", columnIndex))
      ?.classList.remove("txu-focused");
    document
      .querySelector(getSelector("number", rowIndex))
      ?.classList.remove("txu-focused");
  }
};

export const NO_TRANSLATE_INDICATORS = ["***"];

const isHardNoTranslate = (cellValue: string): boolean => {
  return NO_TRANSLATE_INDICATORS.some((indicator) =>
    cellValue.includes(indicator)
  );
};

export const cellIsHardNoTranslate = (cell: Cell): boolean => {
  if (strictCellValueTypeIsString(cell)) {
    return isHardNoTranslate(cell.value);
  }
  if (strictCellValueTypeIsRichText(cell)) {
    return cell.value.richText.some((segment) =>
      isHardNoTranslate(segment.text)
    );
  }
  if (strictCellValueTypeIsHyperlink(cell)) {
    return isHardNoTranslate(cell.value.text || "");
  }

  const cellValue = cell.value as any;
  if (cellValue?.text) {
    return isHardNoTranslate(cellValue.text);
  }
  return false;
};
